import { type ReactNode, useEffect, useRef } from "react";
import { type AutocompleteProps } from "~/common/components/Select/AutocompleteSelect";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { selectSingleFacetConfig } from "~/features/selectionAssistant/housing/redux/selectors";
import { previewTableAutosuggestSx } from "~/features/bulkAddToCart/components/PreviewTable/PreviewTableMaterialIdInput";
import { useTranslation } from "react-i18next";
import { type RuntimeFilterItem } from "~/features/selectionAssistant/housing/types/FilterDefinitionV2";
import { Autocomplete as MUIAutocomplete, TextField } from "~/common/mui";

interface AutosuggestInputProps extends Omit<AutocompleteProps<RuntimeFilterItem, false, false, false>, "renderInput"> {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: boolean;
  "data-test-id"?: string;
  autoFocus?: boolean;
  placeholder?: string;
}

const AutosuggestInput = ({
  label,
  helperText,
  error,
  // "data-test-id": testId,
  autoFocus,
  placeholder,
  classes: outerClasses,
  ...rest
}: AutosuggestInputProps) => {
  const inputRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const outerPaperSx = (outerClasses?.paper as any)?.sx || {};
  const outerListboxSx = (outerClasses?.listbox as any)?.sx || {};

  return (
    <MUIAutocomplete
      {...rest}
      classes={outerClasses}
      slotProps={{
        paper: {
          sx: {
            ...previewTableAutosuggestSx.paper,
            ...outerPaperSx,
          },
        },
        listbox: {
          sx: {
            ...previewTableAutosuggestSx.listbox,
            ...outerListboxSx,
            "& .MuiAutocomplete-option": {
              ...previewTableAutosuggestSx.option,
              ...((outerClasses?.option as any)?.sx || {}),
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          inputRef={autoFocus ? inputRef : undefined}
          {...params}
          label={label}
          helperText={helperText}
          error={error}
          autoComplete={"off"}
          placeholder={placeholder}
        />
      )}
      popupIcon={null}
      getOptionLabel={(item) => item.label}
      renderOption={makeRenderOption}
      filterOptions={(options, config) => {
        const val = rest.inputValue?.toLowerCase();
        return val ? options.filter((option) => option.label.toLowerCase().includes(val)) : options;
      }}
    />
  );
};

interface RHFAutosuggestInputProps extends AutosuggestInputProps {
  name: string;
  renderGA?: (searchDesignation: string) => void;
}

const RHFAutosuggestInput = ({ name, renderGA, ...rest }: RHFAutosuggestInputProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      render={({ field: { value, onChange, onBlur } }) => (
        <AutosuggestInput
          inputValue={value}
          onInputChange={(event, value, reason) => {
            if (reason !== "reset" && reason !== "clear") {
              onChange(value);
            }
          }}
          onBlur={onBlur}
          {...rest}
          onChange={(event, value, reason, details) => {
            if (value || reason === "clear") {
              onChange(value?.code ?? "");
              renderGA && renderGA(value?.label ?? "");
              if (rest.onChange) {
                rest.onChange(event, value, reason, details);
              }
            }
          }}
        />
      )}
      control={control}
      name={name}
    />
  );
};

export interface HousingSAAutocompleteInputProps extends Omit<RHFAutosuggestInputProps, "options" | "label"> {}

export const HousingSAAutocompleteInput = (props: HousingSAAutocompleteInputProps) => {
  const config = useSelector(selectSingleFacetConfig(props.name));

  const btnRef = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation();
  return (
    <>
      <RHFAutosuggestInput
        options={config?.items ?? EMPTY_LIST}
        label={t(config?.label as string)}
        {...props}
        onChange={() => {
          btnRef.current?.click();
        }}
        onReset={() => {
          btnRef.current?.click();
        }}
      />
      <button hidden ref={btnRef} type={"submit"} />
    </>
  );
};

const EMPTY_LIST: RuntimeFilterItem[] = [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const makeRenderOption = (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: RuntimeFilterItem) => {
  return (
    <li {...props}>
      <div data-test-id={`autocomplete-item`}>{option.label}</div>
    </li>
  );
};
